/**
 * The microphone as a loudness meter. Nothing is recorded or sent: the stream feeds an analyser
 * and the level is read once a frame. Opened only while a monkey is paddling, closed after.
 */
export class Mic {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private analyser: AnalyserNode | null = null;
  private buf: Float32Array<ArrayBuffer> | null = null;
  /** 'off' never asked or declined, 'on' granted and open, 'denied' refused */
  state: 'off' | 'on' | 'denied' = 'off';

  static supported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof AudioContext !== 'undefined';
  }

  /** ask for the microphone and start listening; false if refused or unavailable */
  async open(): Promise<boolean> {
    if (this.state === 'on') return true;
    if (!Mic.supported()) return false;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }, video: false });
      this.ctx = new AudioContext();
      const src = this.ctx.createMediaStreamSource(this.stream);
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 512;
      src.connect(this.analyser);
      this.buf = new Float32Array(new ArrayBuffer(this.analyser.fftSize * 4));
      this.state = 'on';
      return true;
    } catch {
      this.state = 'denied';
      this.close();
      return false;
    }
  }

  /** RMS loudness right now, 0 when closed */
  level(): number {
    if (!this.analyser || !this.buf) return 0;
    this.analyser.getFloatTimeDomainData(this.buf);
    let sum = 0;
    for (let i = 0; i < this.buf.length; i++) sum += this.buf[i] * this.buf[i];
    return Math.sqrt(sum / this.buf.length);
  }

  close(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    void this.ctx?.close();
    this.ctx = null;
    this.analyser = null;
    this.buf = null;
    if (this.state === 'on') this.state = 'off';
  }
}
